import { useSearchParams } from "react-router-dom";
import React from "react";
import { Base64Util } from "../../../../shared/utils/base64";
import { useGetFormDetails, useGetRegisteredParticipant, useGetInvoicePaymentRequestDetails } from "./dataloader";
import { ApiPayForPurchaseOrderResponse } from "../../../../shared/types/ApiPayForPurchaseOrderResponse";
import { ApiRegistrationParticipantForParticipantResponse } from "../../../../shared/types/ApiRegistrationParticipantForParticipantResponse";
import QueryParamKey from "../../../../shared/enums/QueryParamKey";

export default function useRegistrationFormService() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [participantObj, setParticipantObj] = React.useState<ApiRegistrationParticipantForParticipantResponse>();

  const { formId, regParticipationId, invoicePaymentRequestId, invoicePayerId, registrationFormType } =
    React.useMemo(() => {
      return {
        formId: Base64Util.decode(searchParams.get(QueryParamKey.FORM_ID)),
        regParticipationId: Base64Util.decode(searchParams.get(QueryParamKey.REG_PARTICIPATION_ID)),
        invoicePaymentRequestId: Base64Util.decode(searchParams.get(QueryParamKey.INVOICE_PAYMENT_REQUEST_ID)),
        invoicePayerId: Base64Util.decode(searchParams.get(QueryParamKey.INVOICE_PAYER_ID)),
        registrationFormType: Base64Util.decode(searchParams.get(QueryParamKey.REGISTRATION_FORM_TYPE))
      };
    }, [searchParams]);

  const { data: regFormDetails } = useGetFormDetails(regParticipationId ? "" : formId, registrationFormType || "");

  const { data: transactionDetails, refetch: refetchInvoicePaymentRequest } = useGetInvoicePaymentRequestDetails(
    invoicePaymentRequestId,
    invoicePayerId
  );

  const { refetch: refetchParticipantDetails } = useGetRegisteredParticipant({
    registrationParticipantId: regParticipationId,
    isEnabled: !participantObj && !!regParticipationId,
    onSuccess: regParticipationDetails => {
      setParticipantObj(regParticipationDetails as ApiRegistrationParticipantForParticipantResponse);
    }
  });

  const setParticipantData = React.useCallback(
    async (data: ApiRegistrationParticipantForParticipantResponse) => {
      setParticipantObj(data);
      searchParams.delete(QueryParamKey.FORM_ID);
      await setSearchParams({
        [QueryParamKey.REG_PARTICIPATION_ID]: Base64Util.encode(data.id) || "",
        ...Object.fromEntries(searchParams)
      });
    },
    [setSearchParams, searchParams]
  );

  const updatePaymentDetails = React.useCallback((paymentDetails: ApiPayForPurchaseOrderResponse) => {
    setParticipantObj(data => {
      if (!paymentDetails) {
        return data;
      }
      return {
        ...data,
        regPurchaseOrder: paymentDetails.purchaseOrder,
        registrationStatus: paymentDetails.registrationStatus,
        confirmationNumber: paymentDetails.confirmationNumber,
        ticketStatus: paymentDetails.ticketStatus,
        myExperienceLoginUrl: paymentDetails.myExperienceLoginUrl,
        badgeSyncApplicable: paymentDetails.badgeSyncApplicable,
        nonAttendeeApplication: paymentDetails.nonAttendeeApplication,
        applicationFlowApprovalPending: paymentDetails.applicationFlowApprovalPending,
        registrationFlowApprovalPending: paymentDetails.registrationFlowApprovalPending,
        participantRegisteredAndTicketNotPending: paymentDetails.participantRegisteredAndTicketNotPending
      } as ApiRegistrationParticipantForParticipantResponse;
    });
  }, []);

  return {
    participantObj,
    updatePaymentDetails,
    setParticipantData,
    regFormDetails,
    formId,
    regParticipationId,
    transactionDetails,
    invoicePaymentRequestId,
    invoicePayerId,
    refetchParticipantDetails,
    refetchInvoicePaymentRequest
  };
}
